import { zodResolver } from "@hookform/resolvers/zod";
import { addDays, differenceInDays, format, parseISO, subHours } from "date-fns";
import { millisecondsInHour } from "date-fns/constants";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { FormField, FormFieldHint, FormTextarea } from "../../form-field/FormField";
import type { ReferralPromotion } from "../data/profile";
import { useMemberProfile } from "../MemberProfileContext";
import { FormShell } from "./ProfileOverlayShell";

const POPUP_LIMIT = 260;
const NOTIFICATION_LIMIT = 150;
const PROMOTION_DAYS = 14;

const referralFormSchema = z.object({
    notificationContent: z
        .string()
        .trim()
        .min(1, "Content is required")
        .max(NOTIFICATION_LIMIT, `Enter at most ${NOTIFICATION_LIMIT} characters`),
    popupContent: z
        .string()
        .trim()
        .min(1, "Content is required")
        .max(POPUP_LIMIT, `Enter at most ${POPUP_LIMIT} characters`),
});

type ReferralFormValues = z.infer<typeof referralFormSchema>;

export function ReferralForm({
    promotion,
    onClose,
}: {
    promotion?: ReferralPromotion;
    onClose: () => void;
}) {
    const { createReferral, editReferral } = useMemberProfile();
    const isView = promotion != null && promotion.status !== "active";
    const {
        handleSubmit,
        register,
        watch,
        formState: { errors },
    } = useForm<ReferralFormValues>({
        resolver: zodResolver(referralFormSchema),
        defaultValues: {
            notificationContent: promotion?.notificationContent ?? "",
            popupContent: promotion?.popupContent ?? "",
        },
    });
    const notificationContent = watch("notificationContent");
    const popupContent = watch("popupContent");
    const start = promotion ? parseISO(promotion.date) : new Date();
    const schedule = getReferralPromotionSchedule(start);

    function onSubmit(values: ReferralFormValues) {
        const payload = {
            notificationContent: { description: values.notificationContent },
            popupContent: { description: values.popupContent },
        };
        if (promotion) {
            editReferral(promotion.id, payload, onClose);
        } else {
            createReferral(payload, onClose);
        }
    }

    return (
        <FormShell
            title={
                promotion
                    ? `Referrals promotion ${format(start, "d MMM yyyy")}`
                    : "Start a new promotion"
            }
            onClose={onClose}
            onSave={isView ? undefined : handleSubmit(onSubmit)}
            saveLabel="Publish"
        >
            {promotion?.status === "active" && (
                <p className="text-xs text-zinc-500 leading-4">
                    The promotion is in progress. The member has already seen the
                    content below so please edit with caution.
                </p>
            )}

            <p className="heading-6 text-zinc-900">Push notification text</p>
            <NotificationTimeline start={start} />
            <FormField
                label="Content"
                htmlFor="promo-notification"
                error={errors.notificationContent?.message}
            >
                <FormTextarea
                    id="promo-notification"
                    rows={3}
                    maxLength={NOTIFICATION_LIMIT}
                    disabled={isView}
                    {...register("notificationContent")}
                    aria-invalid={Boolean(errors.notificationContent) || undefined}
                />
                <FormFieldHint>
                    {characterCount(notificationContent.length, NOTIFICATION_LIMIT)}
                </FormFieldHint>
            </FormField>

            <p className="heading-6 text-zinc-900">Popup text</p>
            <div className="flex items-center gap-2">
                <p className="heading-5 text-zinc-900">
                    You have received a nomination
                </p>
                <Badge variant="secondary" size="sm">
                    {expiresLabel(new Date(schedule.endTime))}
                </Badge>
            </div>
            <FormField
                label="Content"
                htmlFor="promo-popup"
                error={errors.popupContent?.message}
            >
                <FormTextarea
                    id="promo-popup"
                    rows={2}
                    maxLength={POPUP_LIMIT}
                    disabled={isView}
                    {...register("popupContent")}
                    aria-invalid={Boolean(errors.popupContent) || undefined}
                />
                <FormFieldHint>
                    {characterCount(popupContent.length, POPUP_LIMIT)}
                </FormFieldHint>
            </FormField>
        </FormShell>
    );
}

function NotificationTimeline({ start }: { start: Date }) {
    const notifications = getNotificationSchedule(getReferralPromotionSchedule(start));
    const items = [
        {
            time: notifications.initialNotificationTime,
            title: "You have received a nomination",
        },
        ...notifications.notificationsBetweenInitialAndLast.map((item) => ({
            time: item.time,
            title: `Your nomination expires in ${item.daysUntilPromotionEnd} days`,
        })),
        {
            time: notifications.lastNotificationTime,
            title: "Your nomination expires in 24 hours",
        },
    ];

    return (
        <div className="flex flex-col gap-2">
            {items.map((item, index) => (
                <div key={item.time} className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" size="sm">
                            {format(parseISO(item.time), "d MMM")}
                        </Badge>
                        <p className="text-xs text-zinc-900 leading-4">{item.title}</p>
                    </div>
                    {index < items.length - 1 && (
                        <div className="ml-2 h-4 w-px bg-zinc-200" />
                    )}
                </div>
            ))}
        </div>
    );
}

type ReferralPromotionSchedule = {
    startTime: string;
    endTime: string;
    numberOfNotificationsInBetween: number;
};

function getReferralPromotionSchedule(
    now: Date = new Date(),
): ReferralPromotionSchedule {
    return {
        startTime: now.toISOString(),
        endTime: addDays(now, PROMOTION_DAYS).toISOString(),
        numberOfNotificationsInBetween: 1,
    };
}

function getNotificationSchedule(promotionSchedule: ReferralPromotionSchedule) {
    const lastNotificationTime = subHours(
        new Date(promotionSchedule.endTime),
        24,
    ).toISOString();
    const daysInBetweenInitialAndLastNotification = differenceInDays(
        new Date(promotionSchedule.endTime),
        new Date(promotionSchedule.startTime),
    );
    const numberOfNotificationPeriods =
        promotionSchedule.numberOfNotificationsInBetween + 1;
    const daysInBetweenNotifications =
        daysInBetweenInitialAndLastNotification / numberOfNotificationPeriods;

    const notificationsBetweenInitialAndLast = Array.from({
        length: promotionSchedule.numberOfNotificationsInBetween,
    }).map((_, index) => {
        const periodFactor = index + 1;
        const time = addDays(
            new Date(promotionSchedule.startTime),
            daysInBetweenNotifications * periodFactor,
        );

        return {
            time: time.toISOString(),
            daysUntilPromotionEnd: differenceInDays(
                new Date(promotionSchedule.endTime),
                time,
            ),
        };
    });

    return {
        initialNotificationTime: promotionSchedule.startTime,
        lastNotificationTime,
        notificationsBetweenInitialAndLast,
    };
}

function referralPromotionExpiresInDays(endDate: Date, now: Date = new Date()) {
    const msInDay = 24 * millisecondsInHour;
    return Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / msInDay));
}

function expiresLabel(end: Date) {
    const days = referralPromotionExpiresInDays(end);
    return days ? `Expires in ${days} days` : "Expired";
}

function characterCount(current: number, limit: number) {
    return `${current} / ${limit} characters`;
}
